import dayjs from 'dayjs';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { cloneData, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { SyncEvent, SyncEventSource } from '../sync-event.js';
import { EventProfile } from './event-profile.js';

export class EventUpdatedEvent extends SyncEvent {
  static readonly TYPE_ID: string = 'c8d50f9d43fb4f86af04968fc84a0137';

  constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string | null,
    public readonly deviceId: string | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly source: SyncEventSource,
    public readonly event: EventProfile,
    public readonly eventId: string,
  ) {
    super();
  }

  get $typeId(): string {
    return EventUpdatedEvent.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): EventUpdatedEvent {
    return new EventUpdatedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        context.fromData(MapUtils.get<SerializedData>(map, 'event')),
        MapUtils.get<string>(map, 'eventId'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'event': context.toData(this.event),
      'eventId': this.eventId,
    };
  }

  clone(): this {
    return new EventUpdatedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        cloneData(this.event),
        this.eventId,
    ) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        isSameData(other.event, this.event) &&
        other.eventId === this.eventId;
  }
}

export class EventUpdatedEventBuilder extends DataModelBuilder<EventUpdatedEvent> {
  build(context: SerializationContext, map: SerializedData): EventUpdatedEvent {
    return EventUpdatedEvent.fromMap(context, map);
  }
}
